import { Box, Divider } from '@chakra-ui/react';
import NonSelectable from 'components/common/Selectable/NonSelectable';
import Selectable from 'components/common/Selectable/Selectable';
import TextInputSelectable from 'components/common/Selectable/TextInputSelectable';
import { FormLabel } from 'components/UI';
import { IParameter, IProductResource } from '../../../../../server/types';

interface OptionsMenuItemProps {
  parameters: string[];
  params: IParameter[];
  product: IProductResource;
  renderSelectsMenu: boolean;
  isLastGroup: boolean;
  handleUpdateValue(key: number, value: string | boolean | undefined, update: boolean): void;
}

const OptionsMenuItem = ({
  parameters,
  params,
  product,
  renderSelectsMenu,
  handleUpdateValue,
  isLastGroup,
}: OptionsMenuItemProps) => {
  const renderSelectable = (key: number, parameter: IParameter, index: number) => {
    if (!params[key]) {
      return null;
    }
    switch (parameter.parameterType) {
      case 'reference':
        return <NonSelectable parameter={parameter} value={params[key].value} />;
      case 'note':
        return <NonSelectable parameter={parameter} value="" />;
      default:
        return parameter.inputType === 'text' ? (
          <TextInputSelectable
            parameter={parameter}
            onChange={handleUpdateValue}
            paramKey={key}
            key={key}
            value={parameter.type === 'numeric' ? params[key].value.replace(/[^\d.-]/g, '') : params[key].value}
          />
        ) : (
          <Selectable
            parameter={parameter}
            onChange={(value: string | undefined) => handleUpdateValue(key, value, true)}
            key={key}
            options={parameter.values ?? []}
            value={params[key].value ?? ''}
            dropDown
            dropDownProps={{
              menuPlacement: isLastGroup && index === parameters.length - 1 ? 'top' : 'auto',
              minMenuHeight: 200,
            }}
          />
        );
    }
  };

  const renderParameters = parameters.map((key: string, index: number) => {
    const numericKey = Number(key);
    const parameter = params[numericKey];
    if (!parameter) {
      return null;
    }

    const { displayName, parameterType, inputType, isPreloaded } = parameter;

    const isPreload = isPreloaded === undefined || isPreloaded;

    const isSelect = inputType === 'select';
    const isText = inputType === 'text';

    const isInvalidField = product.preload && !renderSelectsMenu && isPreload && isSelect;
    const isInvalidSelectsMenuField = product.preload && renderSelectsMenu && (isText || (isSelect && !isPreload));

    if (!(parameterType !== 'reference' || parameter.visible) || isInvalidField || isInvalidSelectsMenuField) {
      return null;
    }

    const isLast = index === parameters.length - 1;

    const renderDivider = !isLast ? (
      <Divider
        sx={{
          w: 'full',
          borderBottomWidth: 2,
          opacity: 1,
          borderColor: 'base2Colour.400',
          pt: 4,
        }}
      />
    ) : null;

    return (
      <Box key={key} w="full" mt={0} px={4} pt={4} pb={isLast ? 4 : 0}>
        <Box>
          <FormLabel
            sx={{
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              textTransform: 'uppercase',
              fontWeight: 400,
              color: 'base2FontColour.500',
              m: 0,
            }}
            htmlFor={parameter.name}
          >
            {displayName}
          </FormLabel>
        </Box>
        {renderSelectable(numericKey, parameter, index)}
        {renderDivider}
      </Box>
    );
  });

  return <Box bg="base2Colour.500">{renderParameters}</Box>;
};

export default OptionsMenuItem;
